import { useCallback, useEffect, useRef, useState } from "react";

import * as service from "../services/channelPopsService";

const normalizePops = (items) =>
  Array.isArray(items)
    ? items.filter((pop) => pop && typeof pop === "object")
    : [];

const getPopKey = (pop) => {
  if (pop?.popId != null) {
    return `id:${pop.popId}`;
  }

  if (pop?.popTag) {
    return `tag:${pop.popTag}`;
  }

  return null;
};

const mergeUniquePops = (current, incoming) => {
  const map = new Map();

  [...current, ...incoming].forEach((pop) => {
    const key = getPopKey(pop);

    if (key) {
      map.set(key, pop);
    }
  });

  return Array.from(map.values());
};

const parseResponse = (response) => {
  const body = response?.data ?? response;

  if (body?.success === false) {
    throw new Error(body.message || "Unable to load channel Pops.");
  }

  const payload =
    body?.data &&
    typeof body.data === "object" &&
    (Array.isArray(body.data) || Array.isArray(body.data.pops))
      ? body.data
      : body;

  return {
    pops: normalizePops(Array.isArray(payload) ? payload : payload?.pops),
    pagination: payload?.pagination || {},
  };
};

export const useChannelPops = (username, limit = 12) => {
  const pageSize = Math.max(1, Number(limit) || 12);

  const [pops, setPops] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [searching, setSearching] = useState(false);
  const [error, setError] = useState("");
  const [total, setTotal] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");

  const offsetRef = useRef(0);
  const queryRef = useRef("");
  const requestingRef = useRef(false);
  const requestIdRef = useRef(0);

  const fetchPops = useCallback(
    async ({ reset = false, query } = {}) => {
      if (!username) return false;
      if (!reset && requestingRef.current) return false;

      const searchValue =
        query === undefined ? queryRef.current : String(query || "").trim();

      const offset = reset ? 0 : offsetRef.current;
      const requestId = ++requestIdRef.current;

      requestingRef.current = true;

      if (reset) {
        setLoading(true);
        setSearching(Boolean(searchValue));
        setLoadingMore(false);
      } else {
        setLoadingMore(true);
      }

      setError("");

      try {
        const response = searchValue
          ? await service.searchCreatorChannelPops(username, searchValue, {
              limit: pageSize,
              offset,
            })
          : await service.getCreatorChannelPops(username, {
              limit: pageSize,
              offset,
            });

        if (requestId !== requestIdRef.current) {
          return false;
        }

        const { pops: received, pagination } = parseResponse(response);

        const nextOffset =
          Number.isSafeInteger(Number(pagination.nextOffset)) &&
          Number(pagination.nextOffset) > offset
            ? Number(pagination.nextOffset)
            : offset + received.length;

        offsetRef.current = nextOffset;

        setPops((current) =>
          reset
            ? mergeUniquePops([], received)
            : mergeUniquePops(current, received),
        );

        const reportedTotal = Number(pagination.total);
        const hasReportedTotal =
          pagination.total != null && Number.isFinite(reportedTotal);

        setTotal((previous) =>
          hasReportedTotal
            ? reportedTotal
            : reset
              ? received.length
              : Math.max(previous, nextOffset),
        );

        setHasMore(
          typeof pagination.hasMore === "boolean"
            ? pagination.hasMore
            : hasReportedTotal
              ? nextOffset < reportedTotal
              : received.length >= pageSize,
        );

        return {
          pops: received,
          pagination,
        };
      } catch (err) {
        if (requestId !== requestIdRef.current) {
          return false;
        }

        console.error("getCreatorChannelPops failed:", err);

        setError(
          err?.response?.data?.message ||
            err?.message ||
            "Unable to load channel Pops.",
        );

        if (reset) {
          setPops([]);
          setTotal(0);
          setHasMore(false);
        }

        return false;
      } finally {
        if (requestId === requestIdRef.current) {
          requestingRef.current = false;
          setLoading(false);
          setLoadingMore(false);
          setSearching(false);
        }
      }
    },
    [username, pageSize],
  );

  const getCreatorChannelPops = useCallback(
    (options = {}) => fetchPops(options),
    [fetchPops],
  );

  const searchCreatorChannelPops = useCallback(
    (value) => {
      const nextQuery = String(value || "").trim();

      queryRef.current = nextQuery;
      setSearchQuery(nextQuery);

      return fetchPops({
        reset: true,
        query: nextQuery,
      });
    },
    [fetchPops],
  );

  const loadMore = useCallback(() => {
    if (requestingRef.current || !hasMore) {
      return false;
    }

    return fetchPops({
      reset: false,
    });
  }, [fetchPops, hasMore]);

  const refresh = useCallback(
    () =>
      fetchPops({
        reset: true,
      }),
    [fetchPops],
  );

  useEffect(() => {
    requestIdRef.current += 1;
    requestingRef.current = false;

    offsetRef.current = 0;
    queryRef.current = "";

    setPops([]);
    setTotal(0);
    setHasMore(false);
    setError("");
    setSearchQuery("");

    if (username) {
      void fetchPops({
        reset: true,
        query: "",
      });
    } else {
      setLoading(false);
      setLoadingMore(false);
      setSearching(false);
    }

    return () => {
      requestIdRef.current += 1;
      requestingRef.current = false;
    };
  }, [username, fetchPops]);

  return {
    pops,
    total,
    loading,
    loadingMore,
    searching,
    error,
    hasMore,
    searchQuery,
    getCreatorChannelPops,
    searchCreatorChannelPops,
    loadMore,
    refresh,
  };
};

export default useChannelPops;
